import { Button, Disclosure } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  offices,
  type DemergeOfficeResponse,
  type Office,
  type OfficeMergeHistoryEntry,
} from "../../../api/addressBook.service";
import LoadingState from "../../../components/LoadingState";
import { PageHeader } from "../../../components/PageHeader";
import { ROUTES } from "../../../routes";
import styles from "../AddressBook.module.css";
import { WizardStepper } from "../components/WizardStepper";
import introStyles from "../Contacts/WizardIntro.module.css";

type Step = "confirm" | "done";

const STEP_LABELS = ["Confirm", "Done"];

function stepperSteps(current: Step) {
  const order: Step[] = ["confirm", "done"];
  const currentIndex = order.indexOf(current);
  return STEP_LABELS.map((label, i) => ({
    label,
    status:
      i < currentIndex
        ? ("complete" as const)
        : i === currentIndex
          ? ("active" as const)
          : ("pending" as const),
  }));
}

export default function OfficeDemergeWizard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("confirm");
  const [targetOffice, setTargetOffice] = useState<Office | null>(null);
  const [mergeHistory, setMergeHistory] = useState<OfficeMergeHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DemergeOfficeResponse | null>(null);

  useEffect(() => {
    if (!id) return;

    async function loadData() {
      setLoading(true);
      try {
        const [officeRes, historyRes] = await Promise.all([
          offices.getById(id!),
          offices.getMergeHistory(id!),
        ]);
        setTargetOffice(officeRes.data);
        setMergeHistory(historyRes.merges);
      } catch {
        setError("Failed to load office data");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [id]);

  const mergeEntry = mergeHistory[0] ?? null;

  async function handleConfirm() {
    if (!targetOffice || !mergeEntry) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await offices.demerge(targetOffice.id, {
        mergeRelationshipId: mergeEntry.relationship.id,
      });
      setResult(response);
      setStep("done");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to undo merge";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className={styles.wizardWrapper}>
        <PageHeader title="Undo Merge" />
        <LoadingState message="Loading office data..." />
      </div>
    );
  }

  if (!targetOffice) {
    return (
      <div className={styles.wizardWrapper}>
        <PageHeader title="Undo Merge" />
        <div className={styles.formAlert}>Office not found.</div>
      </div>
    );
  }

  if (!mergeEntry) {
    return (
      <div className={styles.wizardWrapper}>
        <PageHeader title="Undo Merge" subtitle={targetOffice.name} />
        <div className={styles.formAlert}>This office has no merge history to undo.</div>
      </div>
    );
  }

  return (
    <div className={styles.wizardWrapper}>
      <PageHeader title="Undo Merge" subtitle={targetOffice.name} />

      <WizardStepper steps={stepperSteps(step)} />

      <div className={styles.wizardContent}>
        {step === "confirm" && (
          <div className={introStyles.intro}>
            <p className={introStyles.oneLiner}>Restores a previously merged office.</p>
            <Disclosure summary="How it works">
              <p className={introStyles.detail}>
                The original office is restored and contacts are moved back. Only works if this
                office has merge history.
              </p>
            </Disclosure>
          </div>
        )}

        {step === "confirm" && (
          <div className={styles.wizardFormContainer}>
            <div className={`${styles.section} ${styles.sectionAccent}`}>
              <h3 className={styles.sectionTitle}>Merge to Undo</h3>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Target Office</span>
                  <span className={styles.infoValue}>{targetOffice.name}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Merged On</span>
                  <span className={styles.infoValue}>
                    {new Date(mergeEntry.relationship.createdAt).toLocaleDateString()}
                  </span>
                </div>
                {mergeEntry.relationship.createdBy && (
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Merged By</span>
                    <span className={styles.infoValue}>{mergeEntry.relationship.createdBy}</span>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.warningBox}>
              <strong>Warning:</strong> The original source office will be restored. Contacts that
              were moved during the merge will be transferred back. This action cannot be easily
              undone.
            </div>

            {error && <div className={styles.formAlert}>{error}</div>}
          </div>
        )}

        {step === "done" && result && (
          <div className={styles.doneContainer}>
            <div className={styles.doneIconWrapper}>
              <span className={styles.doneIcon}>✓</span>
            </div>
            <h3 className={styles.doneTitle}>Merge Reversed</h3>
            <p className={styles.doneMessage}>
              {result.rolesMoved} contact role{result.rolesMoved !== 1 ? "s" : ""} restored to{" "}
              <strong>{result.restoredOffice.name}</strong>
            </p>
          </div>
        )}
      </div>

      <div className={styles.wizardActions}>
        {step === "confirm" && (
          <>
            <Button variant="outlined" color="default" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <div className={styles.wizardActionsRight}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirm}
                disabled={submitting}
              >
                {submitting ? "Reversing..." : "Undo Merge"}
              </Button>
            </div>
          </>
        )}

        {step === "done" && (
          <div className={styles.wizardActionsRight}>
            <Button
              variant="outlined"
              color="default"
              onClick={() => navigate(ROUTES.addressBook.offices)}
            >
              Back to Offices
            </Button>
            {result && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(ROUTES.addressBook.officeDetail(result.restoredOffice.id))}
              >
                View {result.restoredOffice.name}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
